'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FileText, Download, Settings, RefreshCw, Users, TrendingUp } from 'lucide-react';

export function Reports() {
  const [selectedReport, setSelectedReport] = useState('job-analysis');
  const [notificationSettings, setNotificationSettings] = useState({
    jobUpdate: true,
    evaluationDue: true,
    salaryReview: false,
    workshopReminder: true
  });

  const reportData = {
    jobAnalysis: {
      totalJobs: 247,
      completedAnalysis: 189,
      pendingReview: 42,
      outdatedJobs: 16,
      byDepartment: [
        { department: '개발부', completed: 45, pending: 8, outdated: 2 },
        { department: '영업부', completed: 32, pending: 12, outdated: 4 },
        { department: '마케팅부', completed: 28, pending: 8, outdated: 3 },
        { department: '인사부', completed: 15, pending: 6, outdated: 2 },
        { department: '재무부', completed: 8, pending: 3, outdated: 1 }
      ]
    },
    salaryAnalysis: {
      totalEmployees: 324,
      averageSalary: 6850,
      medianSalary: 6200,
      salaryGrowth: 8.5,
      byGrade: [
        { grade: 'L1', count: 45, avgSalary: 3500, minSalary: 3000, maxSalary: 4000 },
        { grade: 'L2', count: 78, avgSalary: 4800, minSalary: 4000, maxSalary: 5500 },
        { grade: 'L3', count: 92, avgSalary: 6200, minSalary: 5500, maxSalary: 7000 },
        { grade: 'L4', count: 67, avgSalary: 8100, minSalary: 7000, maxSalary: 9000 },
        { grade: 'L5', count: 42, avgSalary: 10500, minSalary: 9000, maxSalary: 12000 }
      ]
    },
    organizationalCulture: {
      overallSatisfaction: 78,
      communicationIndex: 82,
      participationRate: 74,
      workLifeBalance: 76,
      careerDevelopment: 71,
      byDepartment: [
        { department: '개발부', satisfaction: 85, communication: 88, participation: 79 },
        { department: '영업부', satisfaction: 76, communication: 78, participation: 72 },
        { department: '마케팅부', satisfaction: 72, communication: 80, participation: 68 },
        { department: '인사부', satisfaction: 89, communication: 92, participation: 86 },
        { department: '재무부', satisfaction: 74, communication: 76, participation: 70 }
      ]
    }
  };

  const upcomingTasks = [
    {
      id: 1,
      task: '개발부 직무분석 업데이트',
      dueDate: '2024.02.15',
      type: '직무분석',
      priority: 'high',
      assignee: '김인사'
    },
    {
      id: 2,
      task: '2024년 1분기 급여 검토',
      dueDate: '2024.02.20',
      type: '급여검토',
      priority: 'medium',
      assignee: '박급여'
    },
    {
      id: 3,
      task: '신규 직무 AI 엔지니어 등록',
      dueDate: '2024.02.25',
      type: '직무등록',
      priority: 'low',
      assignee: '이직무'
    }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold whitespace-nowrap">리포트 & 설정</h1>
          <p className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">분석 리포트 및 설정</p>
        </div>
        <div className="flex space-x-2 flex-shrink-0">
          <Button variant="outline" size="sm" className="text-xs sm:text-sm px-2 sm:px-3">
            <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">다운로드</span>
          </Button>
          <Button size="sm" className="text-xs sm:text-sm px-2 sm:px-3">
            <Settings className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
            <span className="hidden sm:inline">설정</span>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="reports" className="w-full">
        <TabsList>
          <TabsTrigger value="reports" className="text-[10px] sm:text-sm px-2 sm:px-4">분석 리포트</TabsTrigger>
          <TabsTrigger value="tasks" className="text-[10px] sm:text-sm px-2 sm:px-4">업무 관리</TabsTrigger>
          <TabsTrigger value="settings" className="text-[10px] sm:text-sm px-2 sm:px-4">시스템 설정</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="space-y-4 sm:space-y-6">
          {/* Report Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm font-medium whitespace-nowrap">직무분석 완료율</CardTitle>
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">76.5%</div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  189 / 247개 완료
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm font-medium whitespace-nowrap">평균 만족도</CardTitle>
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">78점</div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  전년 대비 +5점
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm font-medium whitespace-nowrap">급여 형평성</CardTitle>
                <Users className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">96.8%</div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  내부 형평성 지수
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm font-medium whitespace-nowrap">업데이트 필요</CardTitle>
                <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">16개</div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  6개월 이상 미갱신
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Report Selection */}
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">상세 분석 리포트</CardTitle>
              <CardDescription className="text-xs sm:text-sm">영역별 상세 분석 결과</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
                <Select value={selectedReport} onValueChange={setSelectedReport}>
                  <SelectTrigger className="w-full sm:w-64 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="job-analysis">직무분석 현황</SelectItem>
                    <SelectItem value="salary-analysis">급여 분석</SelectItem>
                    <SelectItem value="culture-analysis">조직문화 지표</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm w-fit">
                  <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  다운로드
                </Button>
              </div>

              {selectedReport === 'job-analysis' && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h4 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">부서별 직무분석 진행률</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={reportData.jobAnalysis.byDepartment}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="department" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="completed" stackId="a" fill="#3b82f6" name="완료" />
                          <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="검토중" />
                          <Bar dataKey="outdated" stackId="a" fill="#ef4444" name="업데이트 필요" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">전체 진행 현황</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: '완료', value: reportData.jobAnalysis.completedAnalysis },
                              { name: '검토중', value: reportData.jobAnalysis.pendingReview },
                              { name: '업데이트 필요', value: reportData.jobAnalysis.outdatedJobs }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                            fontSize={10}
                          >
                            {[0, 1, 2].map((index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {selectedReport === 'salary-analysis' && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h4 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">직무등급별 급여 분포</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={reportData.salaryAnalysis.byGrade}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="grade" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(value) => [`${value}만원`, '']} />
                          <Bar dataKey="avgSalary" fill="#3b82f6" name="평균급여" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">등급별 인원 분포</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={reportData.salaryAnalysis.byGrade}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="count"
                            nameKey="grade"
                            fontSize={10}
                          >
                            {reportData.salaryAnalysis.byGrade.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {selectedReport === 'culture-analysis' && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h4 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">부서별 만족도 지표</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={reportData.organizationalCulture.byDepartment}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="department" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="satisfaction" fill="#3b82f6" name="전체 만족도" />
                          <Bar dataKey="communication" fill="#10b981" name="소통 지수" />
                          <Bar dataKey="participation" fill="#f59e0b" name="참여율" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div>
                      <h4 className="text-sm sm:text-lg font-semibold mb-3 sm:mb-4">전체 문화 지표</h4>
                      <div className="space-y-3 sm:space-y-4">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm whitespace-nowrap">전체 만족도</span>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Progress value={reportData.organizationalCulture.overallSatisfaction} className="w-20 sm:w-32" />
                            <span className="text-[10px] sm:text-sm font-medium">{reportData.organizationalCulture.overallSatisfaction}점</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm whitespace-nowrap">소통 지수</span>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Progress value={reportData.organizationalCulture.communicationIndex} className="w-20 sm:w-32" />
                            <span className="text-[10px] sm:text-sm font-medium">{reportData.organizationalCulture.communicationIndex}점</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm whitespace-nowrap">참여율</span>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Progress value={reportData.organizationalCulture.participationRate} className="w-20 sm:w-32" />
                            <span className="text-[10px] sm:text-sm font-medium">{reportData.organizationalCulture.participationRate}점</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm whitespace-nowrap">워라밸</span>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Progress value={reportData.organizationalCulture.workLifeBalance} className="w-20 sm:w-32" />
                            <span className="text-[10px] sm:text-sm font-medium">{reportData.organizationalCulture.workLifeBalance}점</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs sm:text-sm whitespace-nowrap">경력개발</span>
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <Progress value={reportData.organizationalCulture.careerDevelopment} className="w-20 sm:w-32" />
                            <span className="text-[10px] sm:text-sm font-medium">{reportData.organizationalCulture.careerDevelopment}점</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">예정된 업무</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무분석 업데이트 및 관리 업무</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap">업무</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">담당</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">마감</TableHead>
                      <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">우선순위</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcomingTasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell>
                          <div className="font-medium text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{task.task}</div>
                          <Badge variant="outline" className="mt-1 text-[10px] sm:text-xs">{task.type}</Badge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm hidden sm:table-cell">{task.assignee}</TableCell>
                        <TableCell className="text-xs sm:text-sm hidden md:table-cell">{task.dueDate}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={
                            task.priority === 'high' ? 'destructive' :
                            task.priority === 'medium' ? 'default' : 'secondary'
                          } className="text-[10px] sm:text-xs">
                            {task.priority === 'high' ? '높음' :
                             task.priority === 'medium' ? '보통' : '낮음'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">직무 소멸·신규 관리</CardTitle>
              <CardDescription className="text-xs sm:text-sm">조직 변화에 따른 직무 생성 및 제거</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium mb-2">신규 직무 제안</h4>
                    <div className="space-y-2">
                      <div className="p-2 sm:p-3 border rounded-lg">
                        <div className="font-medium text-xs sm:text-sm">AI 엔지니어</div>
                        <div className="text-[10px] sm:text-sm text-muted-foreground">개발부 • 제안자: 김개발</div>
                        <div className="flex space-x-2 mt-2">
                          <Button size="sm" variant="outline" className="text-[10px] sm:text-xs h-7 px-2">승인</Button>
                          <Button size="sm" variant="outline" className="text-[10px] sm:text-xs h-7 px-2">반려</Button>
                        </div>
                      </div>
                      <div className="p-2 sm:p-3 border rounded-lg">
                        <div className="font-medium text-xs sm:text-sm">데이터 사이언티스트</div>
                        <div className="text-[10px] sm:text-sm text-muted-foreground">마케팅부 • 제안자: 이마케팅</div>
                        <div className="flex space-x-2 mt-2">
                          <Button size="sm" variant="outline" className="text-[10px] sm:text-xs h-7 px-2">승인</Button>
                          <Button size="sm" variant="outline" className="text-[10px] sm:text-xs h-7 px-2">반려</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium mb-2">소멸 예정 직무</h4>
                    <div className="space-y-2">
                      <div className="p-2 sm:p-3 border rounded-lg">
                        <div className="font-medium text-xs sm:text-sm">웹 퍼블리셔</div>
                        <div className="text-[10px] sm:text-sm text-muted-foreground">개발부 • 마지막 수정: 2023.06.15</div>
                        <div className="flex space-x-2 mt-2">
                          <Button size="sm" variant="outline" className="text-[10px] sm:text-xs h-7 px-2">유지</Button>
                          <Button size="sm" variant="destructive" className="text-[10px] sm:text-xs h-7 px-2">삭제</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">알림 설정</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무 관련 알림 및 리마인더 설정</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">직무분석 업데이트 알림</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground">6개월마다 직무분석 업데이트 알림</div>
                  </div>
                  <Switch
                    checked={notificationSettings.jobUpdate}
                    onCheckedChange={(checked) =>
                      setNotificationSettings(prev => ({ ...prev, jobUpdate: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">직무평가 마감 알림</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground">평가 마감 7일 전 알림</div>
                  </div>
                  <Switch
                    checked={notificationSettings.evaluationDue}
                    onCheckedChange={(checked) =>
                      setNotificationSettings(prev => ({ ...prev, evaluationDue: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">급여 검토 알림</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground">분기별 급여 검토 알림</div>
                  </div>
                  <Switch
                    checked={notificationSettings.salaryReview}
                    onCheckedChange={(checked) =>
                      setNotificationSettings(prev => ({ ...prev, salaryReview: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">워크숍 리마인더</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground">워크숍 시작 1일 전 알림</div>
                  </div>
                  <Switch
                    checked={notificationSettings.workshopReminder}
                    onCheckedChange={(checked) =>
                      setNotificationSettings(prev => ({ ...prev, workshopReminder: checked }))
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">자동 업데이트 설정</CardTitle>
              <CardDescription className="text-xs sm:text-sm">직무분석 및 평가 자동 업데이트 주기</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="text-xs sm:text-sm font-medium">직무분석 업데이트 주기</label>
                    <Select defaultValue="6months">
                      <SelectTrigger className="mt-1 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3months">3개월</SelectItem>
                        <SelectItem value="6months">6개월</SelectItem>
                        <SelectItem value="12months">12개월</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs sm:text-sm font-medium">평가 알림 주기</label>
                    <Select defaultValue="quarterly">
                      <SelectTrigger className="mt-1 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">매월</SelectItem>
                        <SelectItem value="quarterly">분기별</SelectItem>
                        <SelectItem value="biannual">반기별</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button size="sm" className="text-xs sm:text-sm">설정 저장</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">데이터 백업 및 보안</CardTitle>
              <CardDescription className="text-xs sm:text-sm">시스템 데이터 관리</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">자동 백업</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground">매일 자정 자동 백업</div>
                  </div>
                  <Button variant="outline" size="sm" className="text-[10px] sm:text-xs flex-shrink-0">
                    <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    수동 백업
                  </Button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">마지막 백업</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground">2024.01.25 00:00</div>
                  </div>
                  <Badge variant="default" className="text-[10px] sm:text-xs">성공</Badge>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-xs sm:text-sm">데이터 보관 기간</div>
                    <div className="text-[10px] sm:text-sm text-muted-foreground hidden sm:block">삭제된 데이터 보관 기간 설정</div>
                  </div>
                  <Select defaultValue="365days">
                    <SelectTrigger className="w-20 sm:w-32 text-xs sm:text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30days">30일</SelectItem>
                      <SelectItem value="90days">90일</SelectItem>
                      <SelectItem value="365days">1년</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
